import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useDeleteResource } from '@app/src/api/updateHooks'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewAddAnnualSpend from '@app/src/components/Drawer/Views/DrawerViewAddAnnualSpend'
import HasWriteAccess from '@app/src/components/HasWriteAccess'
import LoadingButton from '@app/src/components/LoadingButton'
import { useSpendPeriodOptions } from '@app/src/components/ResourceDetails/spendPeriodOptions'
import ProviderSpendCell from '@app/src/components/Table/Cells/ProviderSpendCell'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import useOrganizationCurrency from '@app/src/hooks/organizationCurrency'
import useOrganizationSettings from '@app/src/hooks/useOrganizationSettings'
import type { Provider, Spend } from '@app/src/types/organizations'
import { comparePeriods } from '@app/src/utils/getOrderedPeriods'
import { NotificationSeverity } from '@app/src/wf-constants'
import {
  Add,
  AddOutlined,
  AttachMoneyOutlined,
  DateRangeOutlined,
  DeleteOutline,
  EditOutlined,
} from '@mui/icons-material'
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'

interface SpendSectionProps {
  provider: Provider
}

const SpendSection: React.FC<SpendSectionProps> = ({ provider }) => {
  const { openDrawer } = useDrawer()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedSpend, setSelectedSpend] = useState<Spend>()
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const open = Boolean(anchorEl)
  const { formatMessage } = useIntl()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()
  const { mutate: deleteAnnualSpend, isPending: isDeleting } = useDeleteResource()
  const { SPEND_PERIOD_OPTIONS } = useSpendPeriodOptions()
  const { organizationCurrency, isLoading } = useOrganizationCurrency()
  const { settings: organizationSettings } = useOrganizationSettings()

  const periodsWithSpend = provider.spends?.map(spend => spend.periodName)
  const periodsWithoutSpend = SPEND_PERIOD_OPTIONS.filter(period => !periodsWithSpend?.includes(String(period.value)))
  const spendPeriodFocus = organizationSettings?.spendPeriodFocus
  const hasFocusedSpend = provider.spends?.some(spend => spend.periodName === spendPeriodFocus)

  const rows: Array<{ spend?: Spend; periodName: string; isFocusedPlaceholder: boolean }> = [
    ...(provider.spends ?? []).map(spend => ({
      spend,
      periodName: spend.periodName,
      isFocusedPlaceholder: false,
    })),
  ]

  if (spendPeriodFocus && !hasFocusedSpend) {
    rows.push({
      periodName: spendPeriodFocus,
      isFocusedPlaceholder: true,
    })
  }

  rows.sort((a, b) => comparePeriods(a.periodName, b.periodName)).reverse()

  const hasRows = rows.length > 0

  const handleOpen = (event: React.MouseEvent<HTMLElement>, spend: Spend) => {
    setSelectedSpend(spend)
    setAnchorEl(event.currentTarget)
  }
  const handleCloseMenu = () => setAnchorEl(null)
  const handleCloseDeleteDialog = () => {
    handleCloseMenu()
    setDeleteDialogOpen(false)
  }

  const handleDelete = () => {
    if (!selectedSpend) return
    deleteAnnualSpend(
      { url: endpoints.providerAnnualSpendDelete(selectedSpend.id) },
      {
        onSuccess: async () => {
          showSnackbar({
            message: formatMessage({ id: 'notifications.successfulResourceDelete' }),
            severity: NotificationSeverity.success,
          })
          queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderCollection] })
          queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderFacets] })
          handleCloseDeleteDialog()
        },
        onError: error => {
          showErrorNotification({ requestError: error, disableAutoClose: true })
        },
      },
    )
  }

  if (isLoading) return <Skeleton variant="rounded" height={80} />

  return (
    <Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle1" fontWeight={600}>
          {formatMessage({ id: 'schemas.supplier.annualSpend.spend' })}
        </Typography>
        <HasWriteAccess>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() =>
              openDrawer(
                <DrawerViewAddAnnualSpend providerId={provider.id} periodsWithoutSpend={periodsWithoutSpend} />,
              )
            }
          >
            {formatMessage({ id: 'schemas.supplier.annualSpend.addPeriod' })}
          </Button>
        </HasWriteAccess>
      </Stack>

      {hasRows ? (
        <Stack sx={{ bgcolor: 'common.white', borderRadius: 2, px: 2, py: 1 }}>
          <Stack divider={<Divider sx={{ opacity: 0.6 }} />}>
            {rows.map(row => (
              <ListItem key={row.spend?.id ?? `focused-placeholder-${row.periodName}`} sx={{ px: 0, py: 1.5 }}>
                {(() => {
                  const spend = row.spend
                  return (
                    <>
                      <ListItemAvatar sx={{ minWidth: 'unset', mr: 2 }}>
                        <DateRangeOutlined color={row.isFocusedPlaceholder ? 'disabled' : 'action'} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography color={row.isFocusedPlaceholder ? 'text.disabled' : 'text.primary'}>
                            {row.periodName}
                          </Typography>
                        }
                        secondary={
                          spend ? (
                            <Stack direction="row">
                              <ProviderSpendCell
                                spend={spend}
                                organizationCurrency={organizationCurrency}
                                disableCell
                              />
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              {formatMessage({ id: 'general.notAdded' })}
                            </Typography>
                          )
                        }
                      />
                      <HasWriteAccess>
                        {spend ? (
                          <IconButton size="small" onClick={e => handleOpen(e, spend)}>
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        ) : (
                          <Button
                            size="small"
                            startIcon={<AddOutlined fontSize="small" />}
                            onClick={() =>
                              openDrawer(
                                <DrawerViewAddAnnualSpend
                                  providerId={provider.id}
                                  periodsWithoutSpend={periodsWithoutSpend}
                                  preselectedPeriodName={row.periodName}
                                />,
                              )
                            }
                          >
                            {formatMessage({ id: 'general.add' })}
                          </Button>
                        )}
                      </HasWriteAccess>
                    </>
                  )
                })()}
              </ListItem>
            ))}
          </Stack>
        </Stack>
      ) : (
        <Stack
          alignItems="center"
          spacing={1.5}
          py={12}
          sx={{ bgcolor: 'grey.100', borderRadius: 2, border: 1, borderColor: 'divider' }}
        >
          <Avatar sx={{ bgcolor: 'grey.200', width: 48, height: 48 }}>
            <AttachMoneyOutlined sx={{ fontSize: 24, color: 'text.disabled' }} />
          </Avatar>
          <Typography variant="subtitle1" fontWeight={600}>
            {formatMessage({ id: 'general.addSpendData' })}
          </Typography>
          <Typography color="text.secondary" textAlign="center" maxWidth={300}>
            {formatMessage({ id: 'general.addSpendDataDescription' })}
          </Typography>
          <HasWriteAccess>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() =>
                openDrawer(
                  <DrawerViewAddAnnualSpend providerId={provider.id} periodsWithoutSpend={periodsWithoutSpend} />,
                )
              }
              sx={{ mt: 1 }}
            >
              {formatMessage({ id: 'schemas.supplier.annualSpend.addPeriod' })}
            </Button>
          </HasWriteAccess>
        </Stack>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem
          onClick={() => {
            openDrawer(
              <DrawerViewAddAnnualSpend
                spend={selectedSpend}
                providerId={provider.id}
                periodsWithoutSpend={periodsWithoutSpend}
              />,
            )
            handleCloseMenu()
          }}
        >
          <ListItemIcon>
            <EditOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>{formatMessage({ id: 'general.edit' })}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseMenu()
            setDeleteDialogOpen(true)
          }}
        >
          <ListItemIcon>
            <DeleteOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText>{formatMessage({ id: 'general.delete' })}</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>{formatMessage({ id: 'general.areYouSure' })}</DialogTitle>
        <DialogContent>{formatMessage({ id: 'schemas.supplier.annualSpend.deleteSpendMessage' })}</DialogContent>
        <DialogActions>
          <Stack direction="row" spacing={2}>
            <Button disabled={isDeleting} onClick={handleCloseDeleteDialog}>
              {formatMessage({ id: 'general.cancel' })}
            </Button>
            <LoadingButton variant="contained" loading={isDeleting} onClick={handleDelete}>
              {formatMessage({ id: 'general.delete' })}
            </LoadingButton>
          </Stack>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default SpendSection
